/**
 * I MARCATORI DI FATTO NON ARRIVANO IN PAGINA, E NEMMENO SOTTO FORMA DI APICE.
 *
 * Per due commit `[F7]` è diventato un apice numerico che rimandava al chip della fonte: un `3`
 * piccolo e rialzato accanto alla frase, con il numero assegnato dal codice leggendo il ledger. La
 * forma era corretta, il difetto era di QUANTITÀ — con sette-otto fonti per pezzo e un marcatore su
 * quasi ogni frase, in pagina finivano venti-trenta apici: lo stesso rumore dell'attribuzione
 * scritta in prosa («secondo il Comune di Santa Fiora»), spostato di mezzo centimetro in alto. Il
 * lettore contava rimandi invece di leggere.
 *
 * LA TRACCIABILITÀ SI ESAURISCE IN FONDO ALLA PAGINA, e lì era già tutta: <SourcesBlock> elenca le
 * fonti e su ognuna dice quante informazioni ne sono state estratte (`posts.sources.factCount`, che
 * il worker conta dal ledger). Chi vuole sapere su cosa è scritto l'articolo ha l'elenco e i numeri;
 * chi legge non ha più niente in mezzo alle frasi.
 *
 * QUINDI IL MARCATORE TORNA A ESSERE UN ARTIFICIO DI CONTROLLO, com'era prima: lo usa il gate
 * (esenzione G7, ancoraggio G6) e subito dopo lo butta `stripFactMarkers` (src/lib/contentGate.ts),
 * che resta l'unico posto in cui la famiglia di forme `[F7]` / `[F7][F8]` / `[F7, F8]` è scritta.
 * Nessuna seconda copia di quel regex vive più qui: due elenchi di forme da tenere allineati a mano
 * erano un `[F9]` stampato in pagina in attesa di succedere.
 *
 * QUESTO MODULO TIENE LE DUE GUARDIE CHE RESTANO, e sono due perché la pagina si difende da due
 * direzioni diverse:
 *
 *  - IN SCRITTURA (`stripModelCitations`): i prompt vietano gli apici — non c'è nessuna numerazione
 *    da imitare — ma è la classe di istruzione che i modelli tengono meno, e la forma si copia
 *    dall'esempio. Un `<sup>2</sup>` arrivato dal rewrite sarebbe un rimando a un numero che in
 *    pagina non esiste da nessuna parte: prima era un'attribuzione FALSA, ora è un segno orfano.
 *    Si butta in entrambi i casi.
 *  - IN LETTURA (`isCitationSup`): un articolo PUBBLICATO porta già gli apici nel suo Lexical —
 *    post 62, l'unico dei 36 in tabella: nodi `text` con `format=64` e testo `[3]`. Riscrivere quel
 *    contenuto significherebbe passare da un update di Posts, cioè accendere gli hook di
 *    pubblicazione (fra cui la ripubblicazione su Facebook) per una modifica di sola resa. Costa
 *    meno, e non tocca niente, riconoscere l'apice in lettura e non stamparlo: lo scartano
 *    <RichTextConverters> per il corpo e <PracticalBox> per la scheda dati.
 */

/**
 * Il bit di Lexical che `<sup>` lascia sul nodo di testo (IS_SUPERSCRIPT, `1 << 6`).
 *
 * Scritto a mano come l'IS_HIGHLIGHT di <RichTextConverters>, e per la stessa ragione: il pacchetto
 * non esporta `nodeFormat` da un percorso pubblico. Verificato sulla catena vera — `<sup>[1]</sup>`
 * esce da convertHTMLToLexical come nodo `text` con `format=64` e testo `"[1]"`.
 */
const IS_SUPERSCRIPT = 1 << 6

/**
 * Il testo di un apice SALVATO dal vecchio flusso: `[3]`, `[2,5]`.
 *
 * LE QUADRE SONO OBBLIGATORIE, e qui la stretta è voluta: questa guardia gira sul contenuto in
 * tabella, dove l'unica cosa che ha scritto un apice numerico è la vecchia `applyCitations`, e
 * quella scriveva sempre le parentesi. Un apice tipografico messo a mano nell'admin —
 * `m<sup>2</sup>`, `1<sup>o</sup>` — non è un rimando: senza le quadre resta l'apice che il
 * converter di default rende, e «m2» non diventa «m».
 */
const SAVED_CITATION_RE = /^\[\s*\d+(?:\s*,\s*\d+)*\s*\]$/

/**
 * Gli apici che il MODELLO scrive, nelle forme che copierebbe da un esempio: `<sup>2</sup>`,
 * `<sup>[7]</sup>`, `<sup>[2,5]</sup>`.
 *
 * QUI LE QUADRE NON SI PRETENDONO, al contrario di `SAVED_CITATION_RE`, ed è la stessa asimmetria di
 * prima: in scrittura si guarda un testo appena consegnato da un LLM, dove la forma senza quadre è
 * la più probabile di tutte. Il prezzo dichiarato è `m<sup>2</sup>`, che cade — nel registro di
 * questi articoli i metri quadri si scrivono «mq», lo prescrive il prompt della scheda dati, e un
 * apice numerico orfano in mezzo a un prezzo è peggio di un «2» perso.
 */
const MODEL_SUP_RE = /<sup\b[^>]*>\s*\[?\s*\d+(?:\s*[,;]\s*\d+)*\s*\]?\s*<\/sup>/gi

/**
 * Un nodo di testo Lexical è un apice di citazione salvato?
 *
 * Chiamata dai due renderer prima di stampare il nodo: se è vero, il nodo non si rende. Lo spazio
 * intorno non va ripulito — l'apice si attaccava alla parola e la punteggiatura gli passava davanti
 * («...del Fiora.³»), quindi togliendolo la frase si richiude da sola.
 */
export function isCitationSup(format: unknown, text: string): boolean {
  if (!((Number(format) || 0) & IS_SUPERSCRIPT)) return false
  return SAVED_CITATION_RE.test(text.trim())
}

/**
 * Butta dall'HTML gli apici numerici scritti dal modello.
 *
 * VA CHIAMATA PRIMA DI `stripFactMarkers`, non dopo, e non è indifferente: togliendo il `<sup>` da
 * «8 euro<sup>2</sup> [F1].» resta «8 euro [F1].», e la passata dei marcatori — che riassorbe lo
 * spazio davanti e la doppia spaziatura — chiude la frase in «8 euro.». Nell'ordine opposto quello
 * spazio doppio arriverebbe in pagina.
 */
export function stripModelCitations(html: string): string {
  return html ? html.replace(MODEL_SUP_RE, '') : html
}
